// src/components/sections/Products.jsx
// Product showcase cards with particle grid background

import React from 'react';
import useInView from '../../hooks/useInView';
import HexIcon from '../ui/HexIcon';
import ParticleGrid from '../ui/ParticleGrid';

// -- Product data --
const PRODUCTS = [
  {
    name: 'TripData',
    logo: '/tripdata.png',
    domain: 'tripdata.co.za',
    url: 'https://tripdata.co.za',
    badge: 'Launching April 2026',
    description:
      'Smart mileage tracking mobile app. Automatic GPS trip logging with 30-second accuracy, SARS-compliant reports, and business vs personal trip classification.',
    features: [
      'Automatic trip detection',
      'SARS-compliant reporting',
      'Business vs personal classification',
      'Route tracking & history',
      'Multi-vehicle support',
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
    platforms: 'Android & iOS',
  },
  {
    name: 'GeoDrive',
    logo: '/geodrive.png',
    domain: 'geodrive.co.za',
    url: 'https://geodrive.co.za',
    description:
      'Trip data intelligence platform. Import trip data from a growing number of supported GPS devices and tracking providers, enrich with smart categorisation, and export SARS-compliant logbooks.',
    features: [
      'Multiple supported device sources',
      'Smart trip categorisation',
      'SARS-compliant logbook export',
      'Zone management & auto-classification',
      'Vehicle expense tracking',
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
    platforms: 'Web, Windows & macOS',
  },
  {
    name: 'GeoFleet',
    logo: '/geofleet.png',
    domain: 'geofleet.co.za',
    url: 'https://geofleet.co.za',
    badge: 'Coming May 2026',
    description:
      'Full lifecycle fleet and team management platform. Track vehicles from acquisition to disposal, manage drivers and teams, monitor real cost-to-service per vehicle, and keep your operation compliant.',
    features: [
      'Full vehicle lifecycle management',
      'Cost-to-service per vehicle',
      'Driver & team management',
      'Maintenance scheduling & alerts',
      'Compliance, licensing & renewals',
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
      </svg>
    ),
    platforms: 'Web, Windows & macOS',
  },
];

// -- Single product card --
const ProductCard = ({ product, index, inView }) => {
  return (
    <div
      className={`group relative rounded-2xl border border-gt-silver-200 bg-white hover:border-teal-300 hover:shadow-gt-glow transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: inView ? `${index * 150}ms` : '0ms' }}
    >
      {/* Hover gradient overlay */}
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-teal-100/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="relative p-8 sm:p-10 flex flex-col h-full items-center text-center">
        {/* Badge */}
        {product.badge && (
          <div className="mb-4">
            <span className="px-3 py-1 text-xs font-semibold rounded-full border bg-teal-50 text-teal-700 border-teal-200">
              {product.badge}
            </span>
          </div>
        )}

        {/* Logo */}
        <div className="mb-6">
          <img
            src={product.logo}
            alt={product.name}
            className="h-16 object-contain"
          />
        </div>

        {/* Content */}
        <h3 className="text-2xl font-display font-bold mb-1 text-gt-dark-100 tracking-tight">
          {product.name}
        </h3>
        <p className="text-sm text-teal-600 mb-1 font-medium">{product.domain}</p>
        <p className="text-xs text-gt-silver-400 mb-4">{product.platforms}</p>
        <p className="text-gt-dark-400 leading-relaxed mb-6 flex-1">{product.description}</p>

        {/* Features */}
        <ul className="space-y-2 mb-8 w-full">
          {product.features.map((f, i) => (
            <li key={i} className="flex items-center justify-center gap-3 text-sm text-gt-dark-300">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
              {f}
            </li>
          ))}
        </ul>

        {/* CTA */}
        {product.noLink ? (
          <button
            disabled
            className="w-full py-3 px-6 rounded-xl text-sm font-medium bg-gt-silver-100 text-gt-silver-400 border border-gt-silver-200 cursor-not-allowed"
          >
            {product.badge || 'Launching Soon'}
          </button>
        ) : (
          <a
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-3 px-6 rounded-xl text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 transition-all duration-200 shadow-gt-teal"
          >
            Visit {product.name}
          </a>
        )}
      </div>
    </div>
  );
};

// -- Main section --
const Products = () => {
  const [headerRef, headerInView] = useInView();
  const [gridRef, gridInView] = useInView({ threshold: 0.05 });

  return (
    <section id="products" className="py-16 md:py-32 relative overflow-hidden">
      {/* Particle grid */}
      <ParticleGrid />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[1] pointer-events-none">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 md:mb-20 transition-all duration-700 ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-4 text-gt-dark-100">
            Our <span className="text-gradient">platforms</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-gt-dark-400">
            Three purpose-built products covering mileage tracking,
            trip data intelligence, and enterprise fleet management.
          </p>
        </div>

        {/* Product cards */}
        <div ref={gridRef} className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
          {PRODUCTS.map((product, i) => (
            <ProductCard key={i} product={product} index={i} inView={gridInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;